import { PRICE_LABEL_MIN_SPACING_PX } from "../constants";

const NICE_MULTIPLIERS = [1, 2, 2.5, 5, 10];
// Tolerance for float noise when checking that a step is a whole number of ticks
const STEP_EPSILON = 1e-9;

const isMultipleOf = (value, divisor) => {
  const ratio = value / divisor;

  return Math.abs(ratio - Math.round(ratio)) < STEP_EPSILON * Math.max(1, ratio);
};

/**
 * The smallest "nice" price step (1, 2, 2.5, 5 × 10ⁿ) that keeps labels at
 * least PRICE_LABEL_MIN_SPACING_PX apart and is a whole number of minimal
 * price moves, so every label is exact at the pair precision.
 */
const chooseStep = (pricePerPixel, minMove) => {
  if (!(pricePerPixel > 0)) return 0;

  const rawStep = Math.max(pricePerPixel * PRICE_LABEL_MIN_SPACING_PX, minMove);
  const magnitude = 10 ** Math.floor(Math.log10(rawStep));
  const step = NICE_MULTIPLIERS.map((multiplier) => multiplier * magnitude).find(
    (candidate) => candidate >= rawStep * (1 - STEP_EPSILON) && isMultipleOf(candidate, minMove),
  );

  return step || NICE_MULTIPLIERS[NICE_MULTIPLIERS.length - 1] * magnitude;
};

/**
 * Prices of horizontal grid lines and price-axis labels inside the pane:
 * evenly spaced multiples of one step.
 *
 * @param {object} priceScale
 * @param {number} minMove - smallest price change of the pair (1 / pricescale)
 * @returns {Array<{ price: number, y: number }>} from top to bottom
 */
export const buildPriceTicks = (priceScale, minMove) => {
  const { height } = priceScale;
  const ticks = [];

  if (height <= 0) return ticks;

  const topPrice = priceScale.yToPrice(0);
  const bottomPrice = priceScale.yToPrice(height);
  const step = chooseStep(priceScale.pricePerPixel, minMove);

  if (!(step > 0) || !Number.isFinite(step)) return ticks;

  for (let multiple = Math.floor(topPrice / step); multiple * step >= bottomPrice; multiple -= 1) {
    const price = multiple * step;

    ticks.push({ price, y: priceScale.priceToY(price) });
  }

  return ticks;
};
